/**
 * @module Schema
 * @description Helpers for schema-related logic
 */

/**
 * @function buildFAQSchema
 * @description Given an array of FAQ objects (with fields 'question' and 'answer'), return a schema object
 */
export const buildFAQSchema = ( questions ) => {
  const schemaObj = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: []
  };

  questions.forEach( ( q ) => {
    const questionObj = {
      '@type': 'Question',
      name: q.title,
      acceptedAnswer: {
        '@type': 'Answer',
        text: typeof q.content === 'string' ? q.content : q.contentRaw
      }
    };
    schemaObj.mainEntity.push( questionObj );
  } );

  return JSON.stringify( schemaObj, null, 2 );
};
export default buildFAQSchema;

/**
 * @function fiberProductSchemas
 * @description returns a product schema object for the fiber internet products
 */
export const fiberProductSchemas = () => {
  const fiber200ProductSchema = JSON.stringify( {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: 'Fiber 200 Mbps Internet',
    image: 'https://frontier.com/dcaas-staticassets/1-3-25-test/images/2024-11-Fiber200-Q3-Hero-Desktop.jpg',
    description: 'Fiber 200 Mbps internet with Frontier Internet Service. A 200 megabit broadband connection, also called Fiber 200 Internet, is an internet service that offers a maximum connection speed of 200 megabits per second (Mbps).',
    brand: {
      '@type': 'Brand',
      name: 'Frontier Internet'
    },
    offers: {
      '@type': 'Offer',
      url: 'https://frontier.com/discount-programs/affordable-connectivity-program',
      priceCurrency: 'USD',
      price: '29.99',
      description: 'Per month with Auto Pay, for 12 months.'
    }
  }, null, 2 );

  const fiber500ProductSchema = JSON.stringify( {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: 'Fiber 500 Mbps Internet',
    image: 'https://frontier.com/dcaas-staticassets/1-15-25/images/2024-12-heroFull_500Mbps_720x600_x2.webp',
    description: 'Fiber 500 Mbps internet with Frontier Internet Service. Basic internet for everyday activities like browsing, streaming and staying connected at home. It’s perfect for sharing on social media, binge streaming shows or gaming with your friends.',
    brand: {
      '@type': 'Brand',
      name: 'Frontier Internet'
    },
    offers: {
      '@type': 'Offer',
      url: 'https://frontier.com/discount-programs/affordable-connectivity-program',
      priceCurrency: 'USD',
      price: '44.99',
      description: 'Per month with Auto Pay, for 12 months.'
    }
  }, null, 2 );

  const fiber1GigProductSchema = JSON.stringify( {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: 'Fiber 1 Gig Internet',
    image: 'https://frontier.com/dcaas-staticassets/1-15-25/images/2024-12-heroFull_1Gig_720x600_x2.webp',
    description: 'Fiber 1 Gig internet with Frontier Internet Service. Reliable speed for active households. Stream, game and work at the same time with confidence. Stream, game, telework and share on social media without worry.',
    brand: {
      '@type': 'Brand',
      name: 'Frontier Internet'
    },
    offers: {
      '@type': 'Offer',
      url: 'https://frontier.com/shop/internet/fiber-internet/gig',
      priceCurrency: 'USD',
      price: '64.99',
      description: 'Per month with Auto Pay, for 12 months.'
    }
  }, null, 2 );

  const fiber2GigProductSchema = JSON.stringify( {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: 'Fiber 2 Gig Internet',
    image: 'https://frontier.com/dcaas-staticassets/1-15-25/images/2024-12-heroFull_2Gig_720x600_x2.webp',
    description: 'Fiber 2 Gig internet with Frontier Internet Service. Ultra-fast and built for advanced productivity, connected devices and high-demand households.',
    brand: {
      '@type': 'Brand',
      name: 'Frontier Internet'
    },
    offers: {
      '@type': 'Offer',
      url: 'https://frontier.com/shop/internet/fiber-internet/2-gig',
      priceCurrency: 'USD',
      price: '99.99',
      description: 'Per month with Auto Pay.'
    }
  }, null, 2 );

  const fiber5GigProductSchema = JSON.stringify( {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: 'Fiber 5 Gig Internet',
    image: 'https://frontier.com/dcaas-staticassets/1-21-25/images/2024-12-heroFull_5Gig_720x600_x2.webp',
    description: 'Fiber 5 Gig internet with Frontier Internet Service. Powerful internet with the best Wi-Fi 7 tech. Supports next-gen gaming, streaming and advanced home setups. Sign up for Fiber 5 Gig and get an Amazon eero Max 7 Wi-Fi router included with your order, a $599 value.',
    brand: {
      '@type': 'Brand',
      name: 'Frontier Internet'
    },
    offers: {
      '@type': 'Offer',
      url: 'https://frontier.com/shop/internet/fiber-internet/5-gig',
      priceCurrency: 'USD',
      price: '129.99',
      description: 'Per month with Auto Pay, for 12 months.'
    }
  }, null, 2 );

  const fiber7GigProductSchema = JSON.stringify( {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: 'Fiber 7 Gig Internet',
    image: 'https://frontier.com/dcaas-staticassets/1-15-25/images/2024-12-heroFull_fiberSpeed7Gig_720x600_x2.webp',
    /* WHWF + will NOT be offered to acquisition customers when eero Pro 7 launches
    description: 'Fiber 7 Gig internet with Frontier Internet Service. Our fastest internet ever. Includes the best Wi-Fi 7 tech, Whole-Home Wi-Fi, Wi-Fi Security and expert tech support. A 7 gigabit broadband connection is an internet service that offers a maximum wired upload and download speed of 7 gigabits per second (Gbps), 7,000 megabits per second (Mbps) or 7 million kilobits per second (Kbps). It’s our fastest fiber internet speed available. ',
    */
    description: 'Fiber 7 Gig internet with Frontier Internet Service. Our fastest internet ever. Includes the best Wi-Fi 7 tech, Wi-Fi Security and expert tech support. A 7 gigabit broadband connection is an internet service that offers a maximum wired upload and download speed of 7 gigabits per second (Gbps), 7,000 megabits per second (Mbps) or 7 million kilobits per second (Kbps). It’s our fastest fiber internet speed available. ',
    brand: {
      '@type': 'Brand',
      name: 'Frontier Internet'
    },
    offers: {
      '@type': 'Offer',
      url: 'https://frontier.com/shop/internet/fiber-internet/7-gig',
      priceCurrency: 'USD',
      price: '199.99',
      description: 'Per month with Auto Pay, for 12 months.'
    }
  }, null, 2 );

  return (
    [
      fiber200ProductSchema,
      fiber500ProductSchema,
      fiber1GigProductSchema,
      fiber2GigProductSchema,
      fiber5GigProductSchema,
      fiber7GigProductSchema
    ]
  );
};

/**
 * @function UnlimitedDigitalVoiceProductSchema
 * @description returns a product schema for the unlimited digital voice product
 */
export const UnlimitedDigitalVoiceProductSchema = () => {
  const schemaObj = {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: 'Unlimited Digital Voice',
    image: 'https://fastly-tundra.frontier.redventures.io/migration/sections/hero/woman-coffee-phone.jpg',
    description: 'A reliable home phone line with caller ID, call waiting, and voicemail. Unlimited nationwide and long-distance calling.',
    brand: {
      '@type': 'Brand',
      name: 'Frontier Internet'
    },
    offers: {
      '@type': 'Offer',
      url: 'https://frontier.com/shop/phone',
      priceCurrency: 'USD',
      price: '25'
    }
  };

  return JSON.stringify( schemaObj, null, 2 );
};
